import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ToggleRow {
  readonly featureToggleId: string;
  readonly featureKey: string;
  readonly featureName: string;
  readonly scope: string;
  readonly status: string;
}

export function M19FeatureTogglePage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ToggleRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [featureKey, setFeatureKey] = useState('');
  const [featureName, setFeatureName] = useState('');
  const [scope, setScope] = useState('GLOBAL');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const load = (): void => {
    apiRequest<{ data: ToggleRow[] }>('/v1/platform/feature-toggles', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const toggle = async (row: ToggleRow): Promise<void> => {
    setBusyId(row.featureToggleId);
    const next = row.status === 'ENABLED' ? 'DISABLED' : 'ENABLED';
    try {
      await apiRequest(`/v1/platform/feature-toggles/${row.featureToggleId}/toggle`, {
        method: 'POST', token, body: { status: next },
      });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setCreated(false);
    try {
      await apiRequest('/v1/platform/feature-toggles', {
        method: 'POST', token,
        body: { featureKey, featureName, scope, status: 'DISABLED' },
      });
      setCreated(true);
      setFeatureKey('');
      setFeatureName('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m19.featureToggle.loading')}</p>;
  return (
    <main aria-label="feature-toggle">
      <h1>{t('m19.featureToggle.title')}</h1>

      <form onSubmit={onCreate} aria-label="feature-toggle-create-form">
        <h2>{t('m19.featureToggle.newToggle')}</h2>
        <label htmlFor="ft-key">{t('m19.featureToggle.labelKey')}</label>
        <input id="ft-key" value={featureKey} onChange={(e) => setFeatureKey(e.target.value)} required />
        <label htmlFor="ft-name">{t('m19.featureToggle.labelName')}</label>
        <input id="ft-name" value={featureName} onChange={(e) => setFeatureName(e.target.value)} required />
        <label htmlFor="ft-scope">{t('m19.featureToggle.labelScope')}</label>
        <select id="ft-scope" aria-label="feature-scope" value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="GLOBAL">{t('m19.featureToggle.optGlobal')}</option>
          <option value="MODULE">{t('m19.featureToggle.optModule')}</option>
          <option value="ENVIRONMENT">{t('m19.featureToggle.optEnvironment')}</option>
          <option value="PILOT">{t('m19.featureToggle.optPilot')}</option>
          <option value="BETA">{t('m19.featureToggle.optBeta')}</option>
        </select>
        {error ? <p role="alert">{error}</p> : null}
        {created ? <p role="status">{t('m19.featureToggle.created')}</p> : null}
        <button type="submit" disabled={creating}>{creating ? t('m19.featureToggle.creating') : t('m19.featureToggle.create')}</button>
      </form>

      <table aria-label="feature-toggles-table">
        <thead><tr><th>{t('m19.featureToggle.colKey')}</th><th>{t('m19.featureToggle.colName')}</th><th>{t('m19.featureToggle.colScope')}</th><th>{t('m19.featureToggle.colStatus')}</th><th>{t('m19.featureToggle.colAction')}</th></tr></thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.featureToggleId}>
              <td>{f.featureKey}</td>
              <td>{f.featureName}</td>
              <td>{f.scope}</td>
              <td>{f.status}</td>
              <td>
                <button
                  type="button"
                  aria-label={`toggle-${f.featureToggleId}`}
                  disabled={busyId === f.featureToggleId}
                  onClick={() => void toggle(f)}
                >
                  {busyId === f.featureToggleId ? t('m19.featureToggle.saving') : f.status === 'ENABLED' ? t('m19.featureToggle.disable') : t('m19.featureToggle.enable')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
